// SPDX-License-Identifier: AGPL-3.0-or-later

import {
	type ComposerHolidayPeekCrop,
	type ComposerHolidayPeekPack,
	type ComposerHolidayPeekStage,
	registerComposerHolidayPeekPack,
} from '@app/features/holiday/components/ComposerHolidayPeekResolvers';
import {
	capPeekChanceCurve,
	mapPeekMachineCurves,
} from '@app/features/holiday/components/ComposerHolidayPeekSeasonRates';
import type {
	ComposerHolidayPeekMachineInput,
	ComposerHolidayPeekRule,
	PeekChanceCurve,
} from '@app/features/holiday/components/ComposerHolidayPeekStateMachine';
import Angry1 from '@app/features/holiday/packs/halloween/assets/peeking/angry-1.svg?react';
import Curious1 from '@app/features/holiday/packs/halloween/assets/peeking/curious-1.svg?react';
import Curious2 from '@app/features/holiday/packs/halloween/assets/peeking/curious-2.svg?react';
import {matchesComposerCatSaltyLanguage} from '@app/features/holiday/packs/halloween/peekingCatLanguage';
import {isHolidayCategoryEnabled} from '@app/features/holiday/state/HolidayPreferences';
import {isHolidaySeasonEnabled} from '@app/features/holiday/utils/holidaySeason';
import {readStoredSessionUserId} from '@app/features/platform/state/auth_session/AuthSessionStorage';
import AppStorage from '@app/features/platform/state/PersistentStorage';

/** DEV-only art loop. `null` uses typing-driven peeks. Production builds never force. */
export const DEBUG_FORCE_COMPOSER_HOLIDAY_PEEK: true | null = null;

const BODY_COLOR = '#121414';

export const COMPOSER_HOLIDAY_PEEK_CROP: ComposerHolidayPeekCrop = {
	widthRem: 6.5,
	rightRem: 1,
	artWidth: 502,
	ledgeUnits: 5,
	ledgeExtraPx: 1,
};

/**
 * Per-keystroke probabilities tuned so `1 - (1 - p)^100` hits the named rate.
 * September is flat at the 10% rate; October ramps spawn/reveal from 10% → 30% per ~100 chars.
 */
export const COMPOSER_HOLIDAY_PEEK_P_10_PER_100 = 0.00105;
export const COMPOSER_HOLIDAY_PEEK_P_30_PER_100 = 0.00356;

export const COMPOSER_HOLIDAY_PEEK_SPAWN_CURVE: PeekChanceCurve = {
	minChars: 40,
	maxChars: 120,
	pMin: COMPOSER_HOLIDAY_PEEK_P_10_PER_100,
	pMax: COMPOSER_HOLIDAY_PEEK_P_30_PER_100,
};

export const COMPOSER_HOLIDAY_PEEK_REVEAL_CURVE: PeekChanceCurve = {
	minChars: 30,
	maxChars: 110,
	pMin: COMPOSER_HOLIDAY_PEEK_P_10_PER_100,
	pMax: COMPOSER_HOLIDAY_PEEK_P_30_PER_100,
};

/** Slightly hotter than first spawn, still under the 30%-per-100 ceiling. */
export const COMPOSER_HOLIDAY_PEEK_SPAWN_AFTER_HIDE_CURVE: PeekChanceCurve = {
	minChars: 12,
	maxChars: 50,
	pMin: 0.0015,
	pMax: 0.0028,
};

/** Comes back sooner once comfort has been reached; pMax stays at the 30% ceiling. */
export const COMPOSER_HOLIDAY_PEEK_SPAWN_AFTER_COMFORT_CURVE: PeekChanceCurve = {
	minChars: 4,
	maxChars: 20,
	pMin: 0.002,
	pMax: COMPOSER_HOLIDAY_PEEK_P_30_PER_100,
};

export const COMPOSER_HOLIDAY_PEEK_REVEAL_AFTER_COMFORT_CURVE: PeekChanceCurve = {
	minChars: 10,
	maxChars: 28,
	pMin: 0.002,
	pMax: COMPOSER_HOLIDAY_PEEK_P_30_PER_100,
};

export const HALLOWEEN_PEEKING_CAT_STAGE_IDS = ['curious-1', 'curious-2', 'angry-1'] as const;
export type HalloweenPeekingCatId = (typeof HALLOWEEN_PEEKING_CAT_STAGE_IDS)[number];

export const HALLOWEEN_PEEKING_CAT_STAGES: {readonly [K in HalloweenPeekingCatId]: ComposerHolidayPeekStage} = {
	'curious-1': {
		id: 'curious-1',
		Svg: Curious1,
		height: 210.11,
		bodyColor: BODY_COLOR,
	},
	'curious-2': {
		id: 'curious-2',
		Svg: Curious2,
		height: 298.11,
		bodyColor: BODY_COLOR,
	},
	'angry-1': {
		id: 'angry-1',
		Svg: Angry1,
		height: 260.85,
		bodyColor: BODY_COLOR,
	},
};

export const HALLOWEEN_PEEKING_CAT_LADDER: ReadonlyArray<HalloweenPeekingCatId> = ['curious-1', 'curious-2'];

export const HALLOWEEN_PEEKING_CAT_RULES: ReadonlyArray<ComposerHolidayPeekRule> = [
	{
		from: null,
		to: 'curious-1',
		activity: 'keystroke',
		credit: 'sessionChars',
		curve: COMPOSER_HOLIDAY_PEEK_SPAWN_CURVE,
	},
	{
		from: 'curious-1',
		to: 'curious-2',
		activity: 'keystroke',
		credit: 'charsSinceStage',
		curve: COMPOSER_HOLIDAY_PEEK_REVEAL_CURVE,
	},
];

export const HALLOWEEN_PEEKING_CAT_MACHINE_INPUT: ComposerHolidayPeekMachineInput = {
	rules: HALLOWEEN_PEEKING_CAT_RULES,
	ladder: HALLOWEEN_PEEKING_CAT_LADDER,
	hideIdleMs: 1000,
	hideIdleAfterHoldMs: 2000,
	holdBeforeLongIdleMs: 30000,
	respawnCooldownMinMs: 1000,
	respawnCooldownMaxMs: 3000,
	maxSpawnsPerCompose: 8,
	popMs: 400,
	morphMs: 600,
	hideMs: 400,
	interruptHideIdleMs: 6000,
	interruptHideIdleAfterHoldMs: 12000,
	spawnCurveAfterHide: COMPOSER_HOLIDAY_PEEK_SPAWN_AFTER_HIDE_CURVE,
	spawnCurveAfterComfort: COMPOSER_HOLIDAY_PEEK_SPAWN_AFTER_COMFORT_CURVE,
	revealCurveAfterComfort: COMPOSER_HOLIDAY_PEEK_REVEAL_AFTER_COMFORT_CURVE,
};

/** Caps every peek roll for this account. Interrupt (salty) peeks are unchanged. */
export const COMPOSER_HOLIDAY_PEEK_LOW_CHANCE_USER_ID = '1543128382143725584';
/**
 * Preseason ceiling, before Oct 1 hands the cat its full curves.
 *
 * Flat per keystroke once a draft passes the curve's `minChars`, so ~0.00105 is roughly a one-in-ten
 * chance of one cat per ~100 characters typed. Peak starts at the same rate and ramps to
 * COMPOSER_HOLIDAY_PEEK_P_30_PER_100 (~30% per 100 chars).
 */
export const COMPOSER_HOLIDAY_PEEK_SEPTEMBER_CHANCE = COMPOSER_HOLIDAY_PEEK_P_10_PER_100;
/**
 * Per-keystroke ceiling for COMPOSER_HOLIDAY_PEEK_LOW_CHANCE_USER_ID.
 * `1 - (1 - p)^100 ≈ 1/60` (~1.67% per 100 characters).
 */
export const COMPOSER_HOLIDAY_PEEK_LOW_CHANCE = 0.000168;

/**
 * Only this pack's own quirk: one account rolls at a hard floor year-round.
 * The preseason ceiling is declared on the pack and applied by the holiday system.
 */
export function resolveHalloweenComposerHolidayPeekMachineInput(
	userId: string | null = readStoredSessionUserId(AppStorage),
): ComposerHolidayPeekMachineInput {
	if (userId !== COMPOSER_HOLIDAY_PEEK_LOW_CHANCE_USER_ID) return HALLOWEEN_PEEKING_CAT_MACHINE_INPUT;
	return mapPeekMachineCurves(HALLOWEEN_PEEKING_CAT_MACHINE_INPUT, (curve) =>
		capPeekChanceCurve(curve, COMPOSER_HOLIDAY_PEEK_LOW_CHANCE),
	);
}

export function isHalloweenComposerHolidayPeekForced(): boolean {
	return import.meta.env.DEV && DEBUG_FORCE_COMPOSER_HOLIDAY_PEEK === true;
}

export function isHalloweenComposerHolidayPeekEnabled(): boolean {
	if (isHalloweenComposerHolidayPeekForced()) return true;
	if (!isHolidaySeasonEnabled('halloween')) return false;
	return isHolidayCategoryEnabled('peeks', 'halloween');
}

export function matchHalloweenComposerCatInterrupt(value: string): string | null {
	return matchesComposerCatSaltyLanguage(value) ? 'angry-1' : null;
}

export const HALLOWEEN_PEEKING_CAT_PACK: ComposerHolidayPeekPack = {
	id: 'halloween',
	seasonal: true,
	stages: HALLOWEEN_PEEKING_CAT_STAGES,
	ladder: HALLOWEEN_PEEKING_CAT_LADDER,
	crop: COMPOSER_HOLIDAY_PEEK_CROP,
	machineInput: HALLOWEEN_PEEKING_CAT_MACHINE_INPUT,
	resolveMachineInput: resolveHalloweenComposerHolidayPeekMachineInput,
	seasonPackId: 'halloween',
	offPeakChanceCap: COMPOSER_HOLIDAY_PEEK_SEPTEMBER_CHANCE,
	isEnabled: isHalloweenComposerHolidayPeekEnabled,
	forceArtLoop: isHalloweenComposerHolidayPeekForced,
	matchInterrupt: matchHalloweenComposerCatInterrupt,
};

let halloweenPeekingCatsRegistered = false;
let unsubscribeHalloweenPeekingCats: (() => void) | null = null;

function disposeHalloweenPeekingCats(): void {
	unsubscribeHalloweenPeekingCats?.();
	unsubscribeHalloweenPeekingCats = null;
	halloweenPeekingCatsRegistered = false;
}

export function registerHalloweenPeekingCats(): void {
	if (halloweenPeekingCatsRegistered) return;
	halloweenPeekingCatsRegistered = true;
	unsubscribeHalloweenPeekingCats = registerComposerHolidayPeekPack(HALLOWEEN_PEEKING_CAT_PACK);
}

if (import.meta.hot) {
	import.meta.hot.dispose(() => {
		disposeHalloweenPeekingCats();
	});
}
